import { type ReactNode, useId, useLayoutEffect, useState } from 'react';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, ButtonBase, Container, Stack, Typography } from '@mui/material';

import { IntakePill } from '../shell/IntakePill';
import { cardSx, CHIP_TONE_TO_PILL, type ChipTone, type PillTone, wf } from '../shell/tokens';

const intakeJourneyStages = ['agreements', 'intake', 'verification', 'go-live'] as const;
export type IntakeJourneyStage = (typeof intakeJourneyStages)[number];
export type IntakeJourneyContent = ReactNode | ((isActive: boolean) => ReactNode);

/** The design's content column: 1080px centred on the page background. */
const COLUMN_WIDTH = 1080;

const labels: Record<IntakeJourneyStage, string> = {
  agreements: 'Agreements & setup',
  intake: 'Intake form',
  verification: 'JotPsych verification',
  'go-live': 'Intake Status',
};

/** The Intake Status step's name, for surfaces that link to it. */
export const INTAKE_STATUS_STAGE_LABEL = labels['go-live'];

export interface IntakeJourneyStageState {
  statusLabel: string;
  /** One-line hint under the status, e.g. what the step covers. */
  caption?: string;
  /** Show the step as done. Only the owner of the stage's completion signal may set this. */
  completed?: boolean;
  /** Chip color. Defaults: completed green, locked grey, selected blue, otherwise grey. */
  tone?: ChipTone;
  lockedReason?: string;
}

/** Presentation only. The caller supplies authoritative status and navigation decisions. */
export interface IntakeJourneyShellProps {
  /** Remove journey chrome without remounting existing intake during a gate fallback. */
  showJourneyChrome?: boolean;
  selectedStage: IntakeJourneyStage;
  onNavigate: (stage: IntakeJourneyStage) => void;
  stages: Record<IntakeJourneyStage, IntakeJourneyStageState>;
  /** Activity callbacks must return a stable tree; pass the flag through instead of unmounting. */
  content: Record<IntakeJourneyStage, IntakeJourneyContent>;
  toolbar?: ReactNode;
  kickoff?: ReactNode;
}

/** A lock pauses previously available content without discarding its local draft. */
function JourneyStageContent({
  content,
  enabled,
  active,
  lockedReason,
}: {
  content: IntakeJourneyContent;
  enabled: boolean;
  active: boolean;
  lockedReason?: string;
}): React.JSX.Element {
  const [hasBeenAvailable, setHasBeenAvailable] = useState(enabled && !lockedReason);
  useLayoutEffect(() => {
    if (!enabled) setHasBeenAvailable(false);
    else if (!lockedReason) setHasBeenAvailable(true);
  }, [enabled, lockedReason]);
  return (
    <>
      {enabled && lockedReason && (
        <Box sx={cardSx}>
          <Typography>{lockedReason}</Typography>
        </Box>
      )}
      <Box
        hidden={Boolean(lockedReason)}
        inert={!active}
        aria-hidden={!active || undefined}
        sx={lockedReason ? undefined : { display: 'contents' }}
      >
        {enabled && (!lockedReason || hasBeenAvailable)
          ? typeof content === 'function'
            ? content(active)
            : content
          : null}
      </Box>
    </>
  );
}

function StepMarker({
  index,
  completed,
  locked,
  selected,
}: {
  index: number;
  completed: boolean;
  locked: boolean;
  selected: boolean;
}) {
  const tone = completed
    ? { bgcolor: wf.green, color: wf.surface, borderColor: wf.green }
    : selected
      ? { bgcolor: wf.blue, color: wf.surface, borderColor: wf.blue }
      : locked
        ? { bgcolor: wf.slateSoft, color: '#a4a4b2', borderColor: wf.slateLine }
        : { bgcolor: '#eeeef6', color: '#7a7a8a', borderColor: 'transparent' };
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        border: '1px solid',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 24px',
        fontSize: 12,
        fontWeight: 700,
        ...tone,
      }}
    >
      {completed ? (
        <CheckOutlinedIcon sx={{ fontSize: 15 }} />
      ) : locked ? (
        <LockOutlinedIcon sx={{ fontSize: 13 }} />
      ) : (
        index + 1
      )}
    </Box>
  );
}

/** Keep supplied content mounted across stage changes; the owner retains scope/save guards. */
export function IntakeJourneyShell({
  showJourneyChrome = true,
  selectedStage,
  onNavigate,
  stages,
  content,
  toolbar,
  kickoff,
}: IntakeJourneyShellProps): React.JSX.Element {
  const id = useId();
  const panels = intakeJourneyStages.map(stage => (
    <Box
      key={stage}
      component="section"
      id={`${id}-${stage}-panel`}
      aria-labelledby={showJourneyChrome ? `${id}-${stage}-button` : undefined}
      hidden={
        showJourneyChrome ? stage !== selectedStage : stage !== 'intake' && stage !== 'go-live'
      }
      sx={{
        minWidth: 0,
        ...(!showJourneyChrome && (stage === 'intake' || stage === 'go-live')
          ? { display: 'contents' }
          : {}),
      }}
    >
      <JourneyStageContent
        content={content[stage]}
        enabled={showJourneyChrome || stage === 'intake' || stage === 'go-live'}
        lockedReason={showJourneyChrome ? stages[stage].lockedReason : undefined}
        active={
          showJourneyChrome
            ? stage === selectedStage && !stages[stage].lockedReason
            : stage === 'intake' || stage === 'go-live'
        }
      />
    </Box>
  ));

  // One element tree in both modes: the gate fallback must not remount the
  // mounted intake form, so only styling and the chrome siblings may change.
  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={showJourneyChrome ? { bgcolor: wf.page, color: wf.ink } : { display: 'contents' }}
    >
      {showJourneyChrome && (
        <Box
          sx={{
            bgcolor: wf.surface,
            borderBottom: `1px solid ${wf.line}`,
            px: { xs: '16px', md: '22px' },
            pt: '20px',
          }}
        >
        <Box sx={{ maxWidth: COLUMN_WIDTH, mx: 'auto' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 0.5, md: 1.5 }}
            sx={{ alignItems: { md: 'baseline' }, flexWrap: 'wrap' }}
          >
            <Typography
              component="h1"
              sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: wf.ink }}
            >
              Get billing-ready
            </Typography>
            <Typography sx={{ fontSize: 13, color: wf.muted }}>
              Four steps. The last one runs three things at once.
            </Typography>
          </Stack>
          {toolbar && <Box sx={{ mt: 1 }}>{toolbar}</Box>}
          <Box
            component="nav"
            aria-label="Practice setup stages"
            sx={{
              mt: '16px',
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              gap: { xs: 1, md: 0 },
              pb: { xs: '16px', md: 0 },
            }}
          >
            {intakeJourneyStages.map((stage, index) => {
              const selected = stage === selectedStage;
              const { statusLabel, caption, completed = false, tone, lockedReason } = stages[stage];
              const locked = Boolean(lockedReason);
              const chipTone: ChipTone = completed
                ? 'ok'
                : locked
                  ? 'mut'
                  : (tone ?? (selected ? 'info' : 'mut'));
              const pillTone: PillTone = CHIP_TONE_TO_PILL[chipTone];
              const last = index === intakeJourneyStages.length - 1;
              return (
                <ButtonBase
                  key={stage}
                  id={`${id}-${stage}-button`}
                  aria-current={selected ? 'step' : undefined}
                  aria-controls={`${id}-${stage}-panel`}
                  aria-describedby={`${id}-${stage}-status`}
                  aria-disabled={locked}
                  onClick={() => {
                    if (!locked) onNavigate(stage);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '9px',
                    textAlign: 'left',
                    minWidth: 0,
                    p: { xs: '12px 14px', md: '12px 10px 13px' },
                    border: { xs: `1px solid ${selected ? wf.blue : wf.line}`, md: 0 },
                    borderRadius: { xs: '8px', md: 0 },
                    borderBottom: { md: `3px solid ${selected ? wf.blue : 'transparent'}` },
                    bgcolor: { xs: selected ? wf.blueSoft : wf.surface, md: 'transparent' },
                    color: locked ? wf.muted : wf.ink,
                    '&.Mui-focusVisible': { outline: `3px solid ${wf.blue}`, outlineOffset: -1 },
                  }}
                >
                  <StepMarker
                    index={index}
                    completed={completed}
                    locked={locked}
                    selected={selected}
                  />
                  <Box
                    sx={{
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '4px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: 1.3,
                        fontWeight: selected ? 700 : 600,
                        letterSpacing: '-0.01em',
                        color: locked ? '#9a9aa6' : selected ? wf.ink : wf.slate,
                      }}
                    >
                      {labels[stage]}
                    </Typography>
                    <IntakePill tone={pillTone} label={locked ? 'Locked' : statusLabel} />
                    <Typography
                      id={`${id}-${stage}-status`}
                      component="p"
                      sx={{
                        fontSize: 12,
                        color: wf.meta,
                        lineHeight: 1.4,
                        textWrap: 'pretty',
                      }}
                    >
                      {lockedReason ?? caption ?? statusLabel}
                    </Typography>
                  </Box>
                  {!last && (
                    <ChevronRightOutlinedIcon
                      aria-hidden="true"
                      sx={{
                        ml: 'auto',
                        alignSelf: 'center',
                        fontSize: 20,
                        color: '#c8c8d4',
                        display: { xs: 'none', md: 'block' },
                      }}
                    />
                  )}
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
        </Box>
      )}
      <Stack
        spacing={showJourneyChrome ? 3 : 0}
        // Gap, not sibling margins: hidden stage panels must not push the visible one down.
        useFlexGap
        sx={
          showJourneyChrome
            ? {
                maxWidth: COLUMN_WIDTH,
                mx: 'auto',
                px: { xs: '16px', md: '22px' },
                pt: '24px',
                pb: { xs: '48px', md: '100px' },
              }
            : { display: 'contents' }
        }
      >
        {panels}
        {showJourneyChrome && kickoff}
      </Stack>
    </Container>
  );
}
